/**
 * Invoice Store
 * Zustand global state management for invoices
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Invoice, LineItem } from '@shared/schemas/invoice.schema';
import type { PaperSize } from '@/types/paper';
import { DEFAULT_PAPER_SIZE } from '@/types/paper';

interface ViewerState {
  zoom: number;        // 0.25 to 4.0
  panX: number;        // horizontal pan offset in pixels
  panY: number;        // vertical pan offset in pixels
  isPanning: boolean;  // is user currently dragging
  currentPage: number; // current page being viewed (1-indexed)
}

interface RateLimitStatus {
  remaining: number;
  resetAt: Date | null;
  isLimited: boolean;
}

interface InvoiceStore {
  // State
  currentInvoice: Invoice | null;
  sessionId: string | null;
  isGenerating: boolean;
  isExporting: boolean;
  error: string | null;

  // Paper size settings
  paperSize: PaperSize;

  // Pagination state (not persisted, calculated from DOM)
  totalPages: number;

  // Rate limit state (not persisted)
  rateLimitStatus: RateLimitStatus;

  // Viewer state (not persisted)
  viewerState: ViewerState;

  // Actions
  setCurrentInvoice: (invoice: Invoice | null) => void;
  setSessionId: (sessionId: string | null) => void;
  setGenerating: (isGenerating: boolean) => void;
  setExporting: (isExporting: boolean) => void;
  setError: (error: string | null) => void;

  // Paper size actions
  setPaperSize: (paperSize: PaperSize) => void;

  // Viewer actions
  setViewerZoom: (zoom: number) => void;
  setViewerPan: (x: number, y: number) => void;
  setViewerPanning: (isPanning: boolean) => void;
  setCurrentPage: (page: number) => void;
  resetViewer: () => void;

  // Pagination actions
  setTotalPages: (pages: number) => void;

  // Rate limit actions
  setRateLimitStatus: (status: RateLimitStatus) => void;

  // Invoice field updates
  updateInvoiceField: <K extends keyof Invoice>(field: K, value: Invoice[K]) => void;

  // Line item operations
  addLineItem: (item?: Partial<LineItem>) => void;
  updateLineItem: (id: string, updates: Partial<LineItem>) => void;
  removeLineItem: (id: string) => void;

  // Computed
  calculateTotals: () => { subtotal: number; discountAmount: number; shipping: number; taxAmount: number; total: number };

  // Reset
  reset: () => void;
}

export const useInvoiceStore = create<InvoiceStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentInvoice: null,
      sessionId: null,
      isGenerating: false,
      isExporting: false,
      error: null,

      // Paper size state
      paperSize: DEFAULT_PAPER_SIZE,

      // Pagination state
      totalPages: 1,

      // Rate limit state
      rateLimitStatus: {
        remaining: 10,
        resetAt: null,
        isLimited: false,
      },

      // Viewer state
      viewerState: {
        zoom: 1.0,
        panX: 0,
        panY: 0,
        isPanning: false,
        currentPage: 1,
      },

  // Actions
  setCurrentInvoice: (invoice) => {
    // Ensure invoice has paperSize, use default if not set
    const invoiceWithPaperSize = invoice
      ? {
          ...invoice,
          paperSize: invoice.paperSize || DEFAULT_PAPER_SIZE,
        }
      : null;

    set({ currentInvoice: invoiceWithPaperSize, error: null });
  },

  setSessionId: (sessionId) => set({ sessionId }),

  setGenerating: (isGenerating) => set({ isGenerating }),

  setExporting: (isExporting) => set({ isExporting }),

  setError: (error) => set({ error }),

  setPaperSize: (paperSize) => set({ paperSize }),

  setTotalPages: (pages) => set({ totalPages: pages }),

  setRateLimitStatus: (rateLimitStatus) => set({ rateLimitStatus }),

  setViewerZoom: (zoom) =>
    set((state) => ({
      viewerState: { ...state.viewerState, zoom },
    })),

  setViewerPan: (x, y) =>
    set((state) => ({
      viewerState: { ...state.viewerState, panX: x, panY: y },
    })),

  setViewerPanning: (isPanning) =>
    set((state) => ({
      viewerState: { ...state.viewerState, isPanning },
    })),

  setCurrentPage: (page) =>
    set((state) => ({
      viewerState: { ...state.viewerState, currentPage: Math.max(1, page) },
    })),

  resetViewer: () =>
    set({
      viewerState: {
        zoom: 1.0,
        panX: 0,
        panY: 0,
        isPanning: false,
        currentPage: 1,
      },
    }),

  updateInvoiceField: (field, value) => {
    const { currentInvoice } = get();
    if (!currentInvoice) return;

    set({
      currentInvoice: {
        ...currentInvoice,
        [field]: value,
      },
    });
  },

  addLineItem: (item) => {
    const { currentInvoice } = get();
    if (!currentInvoice) return;

    const newItem: LineItem = {
      id: crypto.randomUUID(),
      description: item?.description || '',
      quantity: item?.quantity || 1,
      unitPrice: item?.unitPrice || 0,
    };

    set({
      currentInvoice: {
        ...currentInvoice,
        lineItems: [...currentInvoice.lineItems, newItem],
        discountRate: currentInvoice.discountRate ?? 0,
        shipping: currentInvoice.shipping ?? 0,
      },
    });

    // Recalculate totals
    get().calculateTotals();
  },

  updateLineItem: (id, updates) => {
    const { currentInvoice } = get();
    if (!currentInvoice) return;

    const updatedLineItems = currentInvoice.lineItems.map((item: LineItem) =>
      item.id === id
        ? {
            ...item,
            ...updates,
          }
        : item
    );

    set({
      currentInvoice: {
        ...currentInvoice,
        lineItems: updatedLineItems,
      },
    });

    // Recalculate totals
    get().calculateTotals();
  },

  removeLineItem: (id) => {
    const { currentInvoice } = get();
    if (!currentInvoice) return;

    set({
      currentInvoice: {
        ...currentInvoice,
        lineItems: currentInvoice.lineItems.filter((item: LineItem) => item.id !== id),
      },
    });

    // Recalculate totals
    get().calculateTotals();
  },

  calculateTotals: () => {
    const { currentInvoice } = get();
    if (!currentInvoice) return { subtotal: 0, discountAmount: 0, shipping: 0, taxAmount: 0, total: 0 };

    const subtotal = currentInvoice.lineItems.reduce(
      (sum: number, item: LineItem) => sum + (item.quantity * item.unitPrice),
      0
    );
    const discountAmount = (subtotal * currentInvoice.discountRate) / 100;
    const shipping = currentInvoice.shipping || 0;
    const taxAmount = ((subtotal - discountAmount) * currentInvoice.taxRate) / 100;
    const total = subtotal - discountAmount + shipping + taxAmount;

    set({
      currentInvoice: {
        ...currentInvoice,
        subtotal,
      },
    });

    return { subtotal, discountAmount, shipping, taxAmount, total };
  },

  reset: () =>
    set({
      currentInvoice: null,
      sessionId: null,
      isGenerating: false,
      isExporting: false,
      error: null,
      paperSize: DEFAULT_PAPER_SIZE,
      totalPages: 1,
      viewerState: {
        zoom: 1.0,
        panX: 0,
        panY: 0,
        isPanning: false,
        currentPage: 1,
      },
    }),
    }),
    {
      name: 'invoice-storage', // localStorage key
      partialize: (state) => ({
        sessionId: state.sessionId,
        paperSize: state.paperSize,
        // Persist sessionId and paperSize, not viewerState (zoom/pan) or current invoice
      }),
    }
  )
);
